import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { TrendingUp } from "lucide-react";

/**
 * V232.5 → V232.6 — Scan Ads Ticker (success stories during scan loading).
 *
 * Rotates 1 success-story card every 3.5s under the main scan loading
 * progress. Hover pauses, dot indicator allows manual slot jump.
 *
 * V232.6 update: Pulls case studies from `GET /api/public/case-studies`
 * so marketing can edit the rotation (domain, +pct, before/after, quote)
 * from the admin panel without touching code. Falls back to the original
 * hardcoded array when the API is unreachable or returns empty — the
 * ticker must NEVER be empty during a scan (dead air kills conversion).
 */

const FALLBACK_CASE_STUDIES = [
  {
    domain: "zateplenia.eu",
    industry: "Stavebné zateplenie",
    pct: 45, before: 48, after: 70,
    quote: "Z neviditeľnej stránky na top AI odporúčanie v segmente zateplenia.",
  },
  {
    domain: "stavebne-dopyty.sk",
    industry: "Stavebné dopyty portál",
    pct: 25, before: 86, after: 108,
    quote: "ChatGPT nás teraz uvádza ako prvý zdroj pri dopyte na stavebné firmy.",
  },
  {
    domain: "supermajstri.sk",
    industry: "Portál remeselníkov",
    pct: 30, before: 112, after: 146,
    quote: "Perplexity nás cituje 3× častejšie — leady z AI prehľadu rastú každý týždeň.",
  },
];

export default function ScanAdsTicker({ variant = "dark" }) {
  const [items, setItems] = useState(FALLBACK_CASE_STUDIES);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  // Fetch admin-managed case studies on mount.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/case-studies`).then((r) => {
      if (cancelled) return;
      const live = r.data?.items || [];
      if (live.length > 0) setItems(live);
    }).catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % items.length);
    }, 3500);
    return () => clearInterval(t);
  }, [paused, items.length]);

  if (!items.length) return null;
  const current = items[idx % items.length];
  const isDark = variant === "dark";

  const cardCls = isDark
    ? "bg-white/5 border-white/10 text-white"
    : "bg-white border-slate-200 text-slate-900";
  const metaCls = isDark ? "text-gray-400" : "text-slate-500";
  const quoteCls = isDark ? "text-gray-300" : "text-slate-700";

  return (
    <div
      className="w-full max-w-md mx-auto mt-10"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="scan-ads-ticker"
    >
      <div className={`text-[10px] font-bold uppercase tracking-widest ${metaCls} mb-2 text-center font-['JetBrains_Mono']`}>
        Reálne výsledky našich klientov
      </div>
      <div
        key={idx /* re-mount on each slot so CSS keyframes replay */}
        className={`relative rounded-xl border px-5 py-4 ${cardCls} animate-scan-ad-slide overflow-hidden`}
        data-testid={`scan-ad-slot-${idx}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-black text-base tracking-tight" data-testid="scan-ad-domain">
                {current.domain}
              </span>
              <span className={`text-[10px] uppercase tracking-wider ${metaCls}`}>
                · {current.industry}
              </span>
            </div>
            <p className={`text-xs mt-2 leading-relaxed italic ${quoteCls}`} data-testid="scan-ad-quote">
              „{current.quote}“
            </p>
          </div>
          <div className="shrink-0 text-right">
            <div className="inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-400 border border-emerald-400/30 rounded-full px-2 py-0.5 text-[11px] font-black tabular-nums">
              <TrendingUp className="w-3 h-3" />
              +{current.pct}%
            </div>
            <div className={`text-[10px] mt-1.5 ${metaCls} tabular-nums`}>
              <span className="line-through opacity-60">{current.before}</span>
              {" → "}
              <strong className={isDark ? "text-white" : "text-slate-900"}>{current.after}</strong>
              <div className="opacity-70">AI citácií / mes.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Dots indicator */}
      <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="scan-ads-dots">
        {items.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIdx(i)}
            className={`transition-all rounded-full ${
              i === idx
                ? "w-5 h-1.5 bg-[#a855f7]"
                : `w-1.5 h-1.5 ${isDark ? "bg-white/20 hover:bg-white/40" : "bg-slate-300 hover:bg-slate-500"}`
            }`}
            data-testid={`scan-ads-dot-${i}`}
            aria-label={`Case study ${i + 1}`}
          />
        ))}
      </div>

      <style>{`
        @keyframes scan-ad-slide {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-scan-ad-slide {
          animation: scan-ad-slide 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}
